import { MessageType } from 'antd/lib/message'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import {
  Assignee,
  AssigneeType,
  Property,
  PropertyClient,
  PropertyValue as PropertyValueType,
  SelectPropertyValue,
} from 'api/clients/PropertyClient'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { convertPropertyValuesToAssignedPropertyValues } from 'common/PropertyValue/utils'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useWorkflowTransition } from 'hooks/useWorkflowTransition'
import isEqual from 'lodash/isEqual'
import { createContext, FC, useContext, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useFinderContext } from 'screens/Finder/FinderContext'
import { State } from 'stores/Workflows/State'
import { IWorkflow, PersistedPropertyValue } from 'stores/Workflows/Types'
import { Workflow } from 'stores/Workflows/Workflow'
import { MaybeTransitionEffectPayload, runAfterTransitionEffects } from 'utils/workflows/runAfterTransitionEffects'
import { AlmanacFilePermissions, Permission, RootState } from '../../types'
import { useAssignedProperties } from 'hooks/useAssignedProperties'
import {
  InformalFeedbackRequestsClient,
  InformalFeedbackRequestState,
} from 'api/clients/InformalFeedbackRequestsClient'
import { useReviewsRoutes } from 'hooks/useReviewsRoutes'
import { useHistory } from 'react-router-dom'

interface WorkflowStatusProviderProps {
  workflow?: Workflow
  assignee: Assignee
  property?: Property<SelectPropertyValue>
  folder?: IWorkflow['folder']
  permissions?: AlmanacFilePermissions
  skipLoadingAssignedProperties?: boolean
}

interface WorkflowStatusProviderValue {
  property?: Property<SelectPropertyValue>
  currentState?: State
  nextState?: State | null
  updateState: (id: string) => Promise<void>
  isUpdating: boolean
  folder?: IWorkflow['folder']
  canTransitionTo: (id: State['key']) => boolean
}

const WorkflowStatusContext = createContext<WorkflowStatusProviderValue>({
  updateState: () => Promise.resolve(),
  isUpdating: false,
  canTransitionTo: () => false,
})

export const WorkflowStatusProvider: FC<React.PropsWithChildren<WorkflowStatusProviderProps>> = ({
  children,
  workflow,
  assignee,
  property,
  folder,
  skipLoadingAssignedProperties = false,
  permissions = {
    can_transition_to: [],
    can_read: true,
    can_edit: false,
    can_edit_content: false,
    access: Permission.Read,
  },
}) => {
  const history = useHistory()
  const reviewsRoutes = useReviewsRoutes()
  const { refresh: refreshFilesFinder } = useFinderContext()

  const { loadMore: loadAssignedProperties } = useAssignedProperties({
    assignee,
    propertyId: property?.id,
  })

  const propertyValues =
    useSelector(({ filesProperties }: RootState) => {
      if (!workflow) {
        return []
      }
      return filesProperties[assignee.id]?.[workflow.propertyId]?.values
    }, isEqual) || []

  const selectedValue = propertyValues[0]
  const loadingMessageRef = useRef<MessageType | undefined>()
  const [isUpdating, setIsUpdating] = useState(false)
  const workflowTransition = useWorkflowTransition()

  const canTransitionTo = (id: string) => permissions.can_transition_to.includes(id)

  useEffect(() => {
    // if parent already calls loadAssignedProperties to load all of properties this isn't needed
    if (skipLoadingAssignedProperties) return

    loadAssignedProperties()
  }, [assignee, loadAssignedProperties, skipLoadingAssignedProperties])

  if (!selectedValue || !workflow || !property) {
    return (
      <WorkflowStatusContext.Provider
        value={{
          property,
          updateState: () => Promise.resolve(),
          isUpdating: false,
          folder,
          canTransitionTo,
        }}
      >
        {children}
      </WorkflowStatusContext.Provider>
    )
  }

  const currentState = workflow.getState(selectedValue.id + '')
  const nextState: State | null = workflow.getNextState(selectedValue.id + '')
  const transition = async (stateId: string) => {
    const currentState = workflow.getState(selectedValue.id + '')
    const nextState = workflow.getState(stateId)

    return workflowTransition(assignee, currentState, nextState)
  }

  const createAssignedPropertyValue = (propertyValue: PropertyValueType, payloads?: MaybeTransitionEffectPayload) => {
    const assignedPropertyValues = convertPropertyValuesToAssignedPropertyValues(property, [propertyValue])
    if (!assignedPropertyValues) {
      return
    }

    return PropertyClient.updatePropertyAssignedProperty(
      workflow.propertyId,
      assignee,
      assignedPropertyValues,
      payloads,
    )
  }

  const updateState = async (id: string) => {
    if (!canTransitionTo(id)) {
      throw new Error('Unable to update status.')
    }

    try {
      const state = workflow.getState(id)
      const payloads = await transition(state.key)
      if (Object.values(payloads).some((payload) => payload === null)) {
        return Promise.reject('abort')
      }
      setIsUpdating(true)
      loadingMessageRef.current = showToastMessage('Updating status...', 'loading', undefined, 0)
      const response = await createAssignedPropertyValue(state.propertyValue as PersistedPropertyValue, payloads)

      if (response?.type !== SUCCESS_RESPONSE_TYPE) {
        throw new Error()
      }

      runAfterTransitionEffects(assignee, payloads)
      if (payloads['files.move'] || payloads['files.copy']) {
        refreshFilesFinder()
      }

      if (
        (payloads['reviews.create'] || payloads['approvals.create'] || payloads['read_receipts.create']) &&
        assignee.type === AssigneeType.Article
      ) {
        InformalFeedbackRequestsClient.index(assignee.id, undefined, {
          states: [InformalFeedbackRequestState.Open],
          automated: true,
        }).then((response) => {
          if (response.type === SUCCESS_RESPONSE_TYPE) {
            const request = response.data.informal_feedback_requests[0]
            if (!request) {
              return
            }
            history.push({
              pathname: reviewsRoutes.show({ reviewId: request.id }),
            })
          }
        })
      }
    } catch {
      showErrorMessageToast('We were unable to update the status. Our team has been notified.')
    } finally {
      loadingMessageRef.current?.()
      setIsUpdating(false)
    }
  }

  return (
    <WorkflowStatusContext.Provider
      value={{
        property,
        currentState,
        nextState,
        updateState,
        isUpdating,
        folder,
        canTransitionTo,
      }}
    >
      {children}
    </WorkflowStatusContext.Provider>
  )
}

export const useWorkflowStatus = () => {
  return useContext(WorkflowStatusContext)
}
